import type { Deal, Task } from "../types";
import type { DealsByStage } from "./stages";

export type DealSortField = "name" | "created_at" | "due_date" | "index";
export type DealSortOrder = "asc" | "desc";

export interface DealSortConfig {
  field: DealSortField;
  order: DealSortOrder;
}

export const DEFAULT_DEAL_SORT: DealSortConfig = {
  field: "index",
  order: "asc",
};

function getEarliestPendingDueTimestamp(tasks?: Task[]): number | null {
  if (!tasks || tasks.length === 0) return null;
  let earliest: number | null = null;
  for (const t of tasks) {
    if (t.done_date) continue;
    if (!t.due_date) continue;
    const ts = new Date(t.due_date).getTime();
    if (!isNaN(ts)) {
      if (earliest === null || ts < earliest) {
        earliest = ts;
      }
    }
  }
  return earliest;
}

const SMALL_CAPS_MAP: Record<string, string> = {
  ᴀ: "a",
  ʙ: "b",
  ᴄ: "c",
  ᴅ: "d",
  ᴇ: "e",
  ꜰ: "f",
  ɢ: "g",
  ʜ: "h",
  ɪ: "i",
  ᴊ: "j",
  ᴋ: "k",
  ʟ: "l",
  ᴍ: "m",
  ɴ: "n",
  ᴏ: "o",
  ᴘ: "p",
  ꞯ: "q",
  ʀ: "r",
  ꜱ: "s",
  ᴛ: "t",
  ᴜ: "u",
  ᴠ: "v",
  ᴡ: "w",
  ʏ: "y",
  ᴢ: "z",
};

export function normalizeUnicodeFancyName(raw?: string | null): string {
  if (!raw) return "";
  let s = raw.replace(
    /[\u1D00-\u1DBF\u0250-\u02AF\uA700-\uA7FF]/g,
    (ch) => SMALL_CAPS_MAP[ch] || ch,
  );
  // Converte letras em quadrados/círculos (ex: 🄷🄸🄶🄾)
  s = Array.from(s)
    .map((ch) => {
      const cp = ch.codePointAt(0) || 0;
      if (cp >= 0x1f130 && cp <= 0x1f149)
        return String.fromCharCode(65 + (cp - 0x1f130));
      if (cp >= 0x1f150 && cp <= 0x1f169)
        return String.fromCharCode(65 + (cp - 0x1f150));
      if (cp >= 0x1f170 && cp <= 0x1f189)
        return String.fromCharCode(65 + (cp - 0x1f170));
      return ch;
    })
    .join("");
  // NFKD converte fontes matemáticas/cursivas/negrito do WhatsApp (𝓐𝓵𝓵𝓪𝓷, 𝒹𝒾𝑒𝑔𝑜, ʲᵉᵒᵛᵃⁿⁿᵃ) em letras normais
  return s
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function normalizeNameForAlphaSort(raw?: string | null): {
  clean: string;
  startsWithLetter: boolean;
} {
  const deFancied = normalizeUnicodeFancyName(raw);
  const stripped = deFancied
    // Remove caracteres invisíveis, pontuação, emojis ou símbolos no início
    .replace(/^[^a-zA-Z0-9]+/, "")
    // Corrige caso clássico de "0liver" (zero digitado no lugar da letra O antes de letras)
    .replace(/^0(?=[a-zA-Z])/, "O");

  const startsWithLetter = /^[a-zA-Z]/.test(stripped);
  return {
    clean: stripped.toLowerCase(),
    startsWithLetter,
  };
}

export function sortDealsList(
  deals: Deal[],
  sortConfig?: DealSortConfig | null,
  tasksByDealId?: Record<string, Task[]>,
): Deal[] {
  if (!deals || deals.length <= 1) return deals || [];
  const field = sortConfig?.field || "index";
  const order = sortConfig?.order || "asc";
  const dir = order === "asc" ? 1 : -1;

  if (field === "index") {
    return [...deals].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  }

  return [...deals].sort((a, b) => {
    if (field === "name") {
      const normA = normalizeNameForAlphaSort(a.name);
      const normB = normalizeNameForAlphaSort(b.name);

      // Nomes que começam com letras vêm sempre antes de nomes puramente numéricos/vazios
      if (normA.startsWithLetter && !normB.startsWithLetter) return -1;
      if (!normA.startsWithLetter && normB.startsWithLetter) return 1;

      const cmp = normA.clean.localeCompare(normB.clean, "pt-BR", {
        sensitivity: "base",
        numeric: true,
      });
      if (cmp !== 0) return cmp * dir;
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    }

    if (field === "created_at") {
      const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
      const validA = !isNaN(timeA) ? timeA : 0;
      const validB = !isNaN(timeB) ? timeB : 0;
      if (validA !== validB) {
        return (validA - validB) * dir;
      }
      return ((Number(a.id) || 0) - (Number(b.id) || 0)) * dir;
    }

    if (field === "due_date") {
      const dueA = getEarliestPendingDueTimestamp(
        tasksByDealId?.[String(a.id)],
      );
      const dueB = getEarliestPendingDueTimestamp(
        tasksByDealId?.[String(b.id)],
      );

      // Negócios com atividade pendente vêm sempre antes dos negócios sem atividade pendente
      if (dueA !== null && dueB !== null) {
        if (dueA !== dueB) {
          return (dueA - dueB) * dir;
        }
      } else if (dueA !== null && dueB === null) {
        return -1;
      } else if (dueA === null && dueB !== null) {
        return 1;
      }

      const nameA = (a.name || "").trim();
      const nameB = (b.name || "").trim();
      return nameA.localeCompare(nameB, "pt-BR", {
        sensitivity: "base",
        numeric: true,
      });
    }

    return (a.index ?? 0) - (b.index ?? 0);
  });
}

export function sortDealsByStage(
  dealsByStage: DealsByStage,
  sortConfig?: DealSortConfig | null,
  tasksByDealId?: Record<string, Task[]>,
): DealsByStage {
  if (!sortConfig || sortConfig.field === "index") {
    return dealsByStage;
  }

  const sorted: DealsByStage = {};
  for (const stageKey of Object.keys(dealsByStage)) {
    sorted[stageKey] = sortDealsList(
      dealsByStage[stageKey] || [],
      sortConfig,
      tasksByDealId,
    );
  }
  return sorted;
}
